/**
 * Single source of truth for PODGO's SEO & AI-search configuration.
 *
 * The static route catalog and sensible defaults live here in code; admin
 * overrides (per-route index/noindex + title/description, site defaults, and
 * the AI summary) are stored as a JSON blob in `admin_settings` under
 * `SEO_CONFIG_KEY`. Every consumer (runtime <SeoManager>, the Theme Options
 * editor, the live sitemap edge function, and the build-time generator) reads
 * the same shape and merges DB overrides over these defaults, so there is one
 * authoritative model rather than parallel copies.
 */

export const SEO_CONFIG_KEY = "seo_config";

export const SITE_ORIGIN = "https://podgo.io";

export interface RouteSeo {
  /** Whether the page is indexable (sitemap inclusion + robots meta). */
  index: boolean;
  /** Optional per-page title override (replaces the default title). */
  title?: string;
  /** Optional per-page meta-description override. */
  description?: string;
}

export interface SeoDefaults {
  /** Pattern for per-page titles; `%s` is replaced by the page title. */
  titleTemplate: string;
  defaultTitle: string;
  defaultDescription: string;
  /** Absolute URL of the default social share image. */
  ogImage: string;
  organizationName: string;
  /** Plain-text summary served at /llms.txt for AI search engines. */
  aiSummary: string;
}

export interface SeoConfig {
  defaults: SeoDefaults;
  routes: Record<string, RouteSeo>;
}

/** A public route in the catalog, with sitemap hints. */
export interface PublicRoute {
  path: string;
  label: string;
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  priority: string;
}

/**
 * Public, indexable static routes. Auth, onboarding, dashboard and admin
 * routes are intentionally excluded — they must never appear in the sitemap.
 * Keep this aligned with the public routes in src/App.tsx.
 */
export const PUBLIC_ROUTES: PublicRoute[] = [
  { path: "/", label: "Home", changefreq: "weekly", priority: "1.0" },
  { path: "/about", label: "About", changefreq: "monthly", priority: "0.8" },
  { path: "/blog", label: "Blog", changefreq: "weekly", priority: "0.8" },
  {
    path: "/why-you-should-join-podgo",
    label: "Why You Should Join PODGO",
    changefreq: "monthly",
    priority: "0.8",
  },
  { path: "/studios", label: "Studios", changefreq: "weekly", priority: "0.8" },
  { path: "/careers", label: "Careers", changefreq: "weekly", priority: "0.6" },
  { path: "/help", label: "Help Center", changefreq: "monthly", priority: "0.5" },
  { path: "/terms", label: "Terms of Service", changefreq: "yearly", priority: "0.3" },
  { path: "/privacy", label: "Privacy Policy", changefreq: "yearly", priority: "0.3" },
  { path: "/dmca", label: "DMCA Policy", changefreq: "yearly", priority: "0.3" },
  { path: "/cookies", label: "Cookie Policy", changefreq: "yearly", priority: "0.3" },
  { path: "/gdpr", label: "GDPR", changefreq: "yearly", priority: "0.3" },
];

export const DEFAULT_SEO_DEFAULTS: SeoDefaults = {
  titleTemplate: "%s | PODGO",
  defaultTitle: "PODGO | Your Podcast. Everywhere.",
  defaultDescription:
    "Your podcast, distributed everywhere. Upload once, reach every platform.",
  ogImage: `${SITE_ORIGIN}/og-image.png`,
  organizationName: "PODGO",
  aiSummary: `# PODGO

PODGO is a unified podcast operations platform. Podcasters upload episodes once
and PODGO distributes them to every major listening platform, manages the RSS
feed, tracks delivery status, handles revenue and payouts, and offers studio
booking.

## Key pages
- Home: ${SITE_ORIGIN}/
- About: ${SITE_ORIGIN}/about
- Blog: ${SITE_ORIGIN}/blog
- Studios: ${SITE_ORIGIN}/studios
- Careers: ${SITE_ORIGIN}/careers
- Help Center: ${SITE_ORIGIN}/help

PODGO is podcasts only — no music, artists, albums, or tracks.`,
};

/** Default config: every public route indexable, no per-route overrides. */
export const DEFAULT_SEO_CONFIG: SeoConfig = {
  defaults: DEFAULT_SEO_DEFAULTS,
  routes: Object.fromEntries(
    PUBLIC_ROUTES.map((r) => [r.path, { index: true } as RouteSeo]),
  ),
};

/**
 * Merge a stored (partial / possibly stale) config over the code defaults so
 * newly added routes always appear and missing fields fall back gracefully.
 */
export function mergeSeoConfig(raw: unknown): SeoConfig {
  const stored = (typeof raw === "object" && raw !== null ? raw : {}) as Partial<SeoConfig>;
  const routes: Record<string, RouteSeo> = { ...DEFAULT_SEO_CONFIG.routes };
  if (stored.routes && typeof stored.routes === "object") {
    for (const [path, value] of Object.entries(stored.routes)) {
      if (!value || typeof value !== "object") continue;
      routes[path] = {
        index: value.index !== false,
        title: value.title?.trim() || undefined,
        description: value.description?.trim() || undefined,
      };
    }
  }
  return {
    defaults: { ...DEFAULT_SEO_DEFAULTS, ...(stored.defaults ?? {}) },
    routes,
  };
}

/** Parse the raw `admin_settings.value` string into a SeoConfig. */
export function parseSeoConfig(value: string | null | undefined): SeoConfig {
  if (!value) return DEFAULT_SEO_CONFIG;
  try {
    return mergeSeoConfig(JSON.parse(value));
  } catch {
    return DEFAULT_SEO_CONFIG;
  }
}
